import {
  arrayRemove, arrayUnion, collection, doc, getDoc, onSnapshot, query,
  runTransaction, serverTimestamp, setDoc, updateDoc, where, writeBatch,
  type WithFieldValue,
} from "firebase/firestore";
import { daPlataforma, db, functions } from "./ambiente";
import { httpsCallable } from "firebase/functions";
import type { ChallengeConfig, ChallengeEvent } from "@nhomi/core";
import { perfilQueVale } from "./users";
import { emptyMember, type ChallengeDoc, type UserDoc } from "./schema";

export interface Challenge extends ChallengeDoc { id: string }

const challengeRef = (cid: string) => doc(db, "challenges", cid);
const memberRef = (cid: string, uid: string) => doc(db, "challenges", cid, "members", uid);
const userRef = (uid: string) => doc(db, "users", uid);

/* ---------- códigos de convite ---------- */

// Sem I, O, 0 e 1: o código é lido em voz alta e digitado no zap.
const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function newCode(len = 6): string {
  /* O ALEATÓRIO VEM DA PLATAFORMA: `crypto.getRandomValues` não existe
     no React Native, e o `expo-crypto` não existe no navegador. Os dois
     são o aleatório do sistema, e é ele que garante que dois grupos
     criados no mesmo segundo não ganhem o mesmo código — `Math.random`
     semeado igual já produziu colisão em app de convite. */
  const bytes = daPlataforma().bytesAleatorios(len);
  return Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join("");
}

/* ---------- perfil ---------- */

export interface Me { uid: string; name: string; photo: string; email: string }

/**
 * Garante o documento do usuário.
 * Mantém nome e foto do Google em dia sem sobrescrever apelido escolhido
 * à mão — quem trocou o nome no app não quer ver o do Google de volta.
 */
export async function ensureUser(me: Me): Promise<UserDoc> {
  const ref = userRef(me.uid);
  const snap = await getDoc(ref);
  const base = { name: me.name, photo: me.photo, email: me.email };

  if (!snap.exists()) {
    const fresh: UserDoc = { ...base, challengeIds: [] };
    await setDoc(ref, { ...fresh, createdAt: serverTimestamp() });
    return fresh;
  }

  const data = snap.data() as UserDoc;
  const patch: Partial<UserDoc> = {};
  if (!data.nickname && data.name !== base.name) patch.name = base.name;
  if (!data.customPhoto && data.photo !== base.photo) patch.photo = base.photo;
  if (Object.keys(patch).length) await updateDoc(ref, patch);
  return { ...data, ...patch };
}

/* ---------- desafios ---------- */

export function watchMyChallenges(
  uid: string,
  cb: (list: Challenge[]) => void,
): () => void {
  const q = query(collection(db, "challenges"), where("memberUids", "array-contains", uid));
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as ChallengeDoc) }));
      list.sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0));
      cb(list);
    },
    () => cb([]),
  );
}

export function watchChallenge(cid: string, cb: (c: Challenge | null) => void): () => void {
  return onSnapshot(
    challengeRef(cid),
    (s) => cb(s.exists() ? { id: s.id, ...(s.data() as ChallengeDoc) } : null),
    () => cb(null),
  );
}

export async function createChallenge(
  me: Me,
  input: { name: string; description: string; startDay: string; endDay: string },
): Promise<string> {
  const cid = doc(collection(db, "challenges")).id;

  // Reserva o código numa transação: dois desafios criados no mesmo
  // segundo não podem cair no mesmo convite.
  let code: string | null = null;
  for (let i = 0; i < 6 && !code; i++) {
    const candidate = newCode();
    try {
      await runTransaction(db, async (tx) => {
        const ref = doc(db, "codes", candidate);
        if ((await tx.get(ref)).exists()) throw new Error("colisão");
        tx.set(ref, { challengeId: cid, createdAt: serverTimestamp() });
      });
      code = candidate;
    } catch { /* tenta outro */ }
  }
  if (!code) throw new Error("Não consegui gerar um código de convite. Tenta de novo.");

  /* O nome e a foto da ficha são os de `users/{uid}`: os do `me` são os
     do login, que a troca em Conta não alcança. */
  const autor = await perfilQueVale(me);

  const batch = writeBatch(db);
  // `WithFieldValue` e não `ChallengeDoc`: na ESCRITA um carimbo de tempo é
  // um FieldValue e só vira Timestamp na leitura. Sem isso o `satisfies`
  // recusa — e sem o `satisfies` a gente perde a garantia de que o
  // documento sai com todos os campos.
  batch.set(challengeRef(cid), {
    name: input.name,
    description: input.description,
    startDay: input.startDay,
    endDay: input.endDay,
    code,
    ownerUid: me.uid,
    memberUids: [me.uid],
    coverPath: null,
    createdAt: serverTimestamp(),
  } satisfies WithFieldValue<ChallengeDoc>);
  batch.set(memberRef(cid, me.uid), {
    ...emptyMember(autor.name, autor.photo, "owner"),
    joinedAt: serverTimestamp(),
  });
  batch.set(userRef(me.uid), { challengeIds: arrayUnion(cid) }, { merge: true });
  await batch.commit();

  return cid;
}

export class JoinError extends Error {}

/**
 * Entrar com código.
 *
 * A ORDEM aqui é a correção de um bug que travou a primeira pessoa a entrar
 * no app anterior: a ficha de membro só é legível por quem já é membro, e o
 * código antigo lia a ficha ANTES de entrar. Ler o desafio serve de teste —
 * quem não é membro é recusado, e só então entramos.
 */
export async function joinByCode(me: Me, rawCode: string): Promise<string> {
  const code = rawCode.trim().toUpperCase();
  if (code.length < 4) throw new JoinError("Código inválido.");

  const codeSnap = await getDoc(doc(db, "codes", code));
  if (!codeSnap.exists()) throw new JoinError("Não achei nenhum desafio com esse código.");
  const cid = codeSnap.data()["challengeId"] as string;

  const jaEra = await getDoc(challengeRef(cid)).then((s) => s.exists()).catch(() => false);
  if (!jaEra) {
    await updateDoc(challengeRef(cid), { memberUids: arrayUnion(me.uid) });
  }

  // Agora dá para olhar a ficha sem risco de zerar o placar de quem volta.
  let ficha = null;
  try { ficha = await getDoc(memberRef(cid, me.uid)); } catch { /* ramo conservador */ }

  /* Nome e foto vêm do DOCUMENTO, não do `me`: o `me` é o perfil do
     login — o original do Google — e gravá-lo na ficha de quem voltava
     apagava o apelido e a foto escolhidos em Conta. */
  const autor = await perfilQueVale(me);

  const batch = writeBatch(db);
  if (ficha && !ficha.exists()) {
    batch.set(memberRef(cid, me.uid), {
      ...emptyMember(autor.name, autor.photo, "member"),
      joinedAt: serverTimestamp(),
    });
  } else {
    // Sem certeza de que a ficha é nova, mexe só no que é perfil. O placar
    // é do servidor e zerá-lo por engano seria bem pior que não atualizar.
    batch.set(memberRef(cid, me.uid), { name: autor.name, photo: autor.photo }, { merge: true });
  }
  batch.set(userRef(me.uid), { challengeIds: arrayUnion(cid) }, { merge: true });
  await batch.commit();

  return cid;
}

export async function leaveChallenge(cid: string, uid: string): Promise<void> {
  const batch = writeBatch(db);
  batch.update(challengeRef(cid), { memberUids: arrayRemove(uid) });
  batch.set(userRef(uid), { challengeIds: arrayRemove(cid) }, { merge: true });
  await batch.commit();
}

/* ---------- editar ---------- */

export type EdicaoDoDesafio = Partial<
  Pick<ChallengeDoc, "name" | "description" | "startDay" | "endDay" | "coverPath" | "interacao">
>;

export async function updateChallenge(cid: string, patch: EdicaoDoDesafio): Promise<void> {
  await updateDoc(challengeRef(cid), patch);
}

/**
 * Grava a configuração de pontos, refeições, selos e eventos.
 *
 * Vai num `update` só de propósito: a função que refaz o placar de todo
 * mundo dispara a cada escrita no desafio, e salvar campo a campo faria
 * o grupo inteiro ser recalculado quatro vezes seguidas.
 */
export async function updateConfig(
  cid: string,
  config: Pick<ChallengeConfig, "rules" | "meals" | "cuisines" | "events">,
): Promise<void> {
  await updateDoc(challengeRef(cid), {
    rules: config.rules,
    meals: config.meals,
    cuisines: config.cuisines,
    events: config.events,
  });
}

/* ---------- uma missão por vez (tela de Missões) ---------- */

/**
 * Mexe numa missão só, numa transação: lê a lista atual e grava de
 * volta. Mexer pela lista inteira (como faz a tela de Regras) atropelaria
 * uma missão criada por outra aba ou renovada pelo servidor na segunda.
 */
async function mexerNasMissoes(
  cid: string, f: (events: ChallengeEvent[]) => ChallengeEvent[],
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(challengeRef(cid));
    const events = ((snap.data()?.["events"] ?? []) as ChallengeEvent[]).map((e) => ({ ...e }));
    tx.update(challengeRef(cid), { events: f(events) });
  });
}

export const adicionarMissao = (cid: string, ev: ChallengeEvent) =>
  mexerNasMissoes(cid, (l) => [...l.filter((e) => e.id !== ev.id), ev]);

/** Tira (ou devolve) uma pessoa de uma missão. */
export const tirarDaMissao = (cid: string, eventId: string, uid: string, tirar: boolean) =>
  mexerNasMissoes(cid, (l) => l.map((e) => {
    if (e.id !== eventId) return e;
    const fora = new Set(e.fora ?? []);
    if (tirar) fora.add(uid); else fora.delete(uid);
    return { ...e, fora: [...fora] };
  }));

/**
 * Encerra hoje: a janela fecha no fim do dia e a repetição para. O que
 * já foi ganho fica — apagar a missão tiraria o bônus de quem fechou.
 */
export const encerrarMissao = (cid: string, eventId: string, hoje: string) =>
  mexerNasMissoes(cid, (l) => l.map((e) => {
    if (e.id !== eventId) return e;
    const { repetir: _r, ...resto } = e;
    return { ...resto, to: e.to && e.to < hoje ? e.to : hoje };
  }));

/** Quem pode comentar e dar nota no desafio (só o dono muda). */
export async function definirInteracao(cid: string, interacao: "todos" | "dono" | "ninguem"): Promise<void> {
  await updateDoc(challengeRef(cid), { interacao });
}

/**
 * Remove alguém do desafio (só o dono). `banir` = "não deixar voltar".
 * Quem decide e faz é o servidor (`functions/src/membros.ts`).
 */
export async function removerDoDesafio(cid: string, alvo: string, banir: boolean): Promise<void> {
  const chamar = httpsCallable<{ cid: string; alvo: string; banir: boolean }, { ok: boolean }>(
    functions, "nhomiRemoverDoDesafio");
  await chamar({ cid, alvo, banir });
}
